/**
 * Investigation Plan Types
 * Based on Vercel prototype: https://v0-vault-investigations.vercel.app/
 */

export enum InvestigationStatus {
  IDLE = 'idle',
  GENERATING = 'generating',
  READY = 'ready',
  ACCEPTED = 'accepted',
  FAILED = 'failed',
}

export interface InvestigationStep {
  id: string;
  order: number;
  title: string;
  description: string;
  hasSpecialAction?: boolean; // e.g., "View Findings" link or scheduling sparkle
  status?: 'pending' | 'in_progress' | 'complete' | 'skipped';
}

export interface PolicyReference {
  policyId: string;
  title: string;
  clauses?: string[]; // e.g., ["4.2", "5.1"]
  relevance?: string;
}

export interface TargetedQuestion {
  id: string;
  stakeholderType: 'WITNESSES' | 'ANONYMOUS_REPORTER' | 'SUBJECT';
  stakeholderName?: string; // e.g., "Former Team Members", "David Miller"
  questions: string[];
}

export interface PolicyExcerpt {
  id: string;
  title: string; // e.g., "Respect in the Workplace v2.1 (Clause 4.2)"
  excerpt: string; // the flagged policy text
}

export interface PolicyFindings {
  excerpts: PolicyExcerpt[];
  analysis: string; // AI analysis & case implications
}

export interface WitnessScheduling {
  stepId: string; // which investigation step this relates to
  witnesses: Array<{
    name: string;
    role?: string;
    email?: string;
  }>;
  subject: string;
  message: string;
  suggestedTimes?: Array<{
    day: string; // e.g., "Today", "Tomorrow"
    time: string; // e.g., "3:30 PM"
    duration: string; // e.g., "30 min"
    availability: 'high' | 'medium' | 'low';
  }>;
  teamsLinkGenerated?: boolean;
}

export interface InvestigationPlan {
  id: string;
  caseId: string;
  caseDisplayId: string; // e.g., "CF2603C0"
  status: InvestigationStatus;
  generatedAt?: Date;
  acceptedAt?: Date;

  // Plan content
  steps: InvestigationStep[];
  checklist?: string; // markdown checklist from LLM, e.g. "- [ ] Step 1\n- [ ] Step 2"
  policies: PolicyReference[];
  targetedQuestions: TargetedQuestion[];

  // Policy Audit findings (shown behind the "View Findings" link)
  policyFindings?: PolicyFindings;

  // Scheduling
  scheduling?: WitnessScheduling;

  // Next best action (derived from first incomplete step)
  nextBestAction?: {
    stepId: string;
    title: string;
    description: string;
    actionType: 'schedule' | 'review' | 'interview' | 'document';
  };
}

export interface InvestigationPlanModalProps {
  open: boolean;
  caseId: string;
  caseDisplayId: string;
  plan: InvestigationPlan | null;
  onClose: () => void;
  onAccept: () => void;
  onEditPlan: () => void;
}

export interface WorkspaceInvestigationProps {
  caseId: string;
  plan: InvestigationPlan | null;
  onChecklistUpdate: (updatedMarkdown: string) => void;
}

export interface ScheduleInterviewModalProps {
  open: boolean;
  caseId: string;
  caseDisplayId: string;
  scheduling: WitnessScheduling;
  onClose: () => void;
  onSendInvitations: (selectedTime?: string) => void;
}
